import {
	httpPut,
	HTTPDestinationType,
	httpGet,
} from '../lib/adapters/httpAdapter'
import { getEnvVariable } from './getenvService'
import { getTncVersion } from './getStaticTnCService'
import {
	UserCorePrefsQuery,
	UserBusIntelPrefsQuery,
	UserCorePreferences,
	UserBusIntelPreferences,
	UserAgentProdPreferences,
	DEFAULTBUSINTELPREFS,
	UserRecruitPreferences,
	DEFAULTRECRUITPREFS,
	UserRecruitPrefsQuery,
	DEFAULTAGENTPRODPREFS,
} from './models/userPreferences'
import { assertApiErrorOrThrow } from '../lib/errors/apiError'

/**
 * @param queryParams - An object containing the partyId and preferencesTypeCd.
 * @returns A promise that resolves with the response from the HTTP PUT request.
 * @throws Error if the HTTP PUT request fails or if an unknown error occurs.
 */
export async function putUserCorePrefs(
	queryParams: UserCorePrefsQuery,
	token?: string
) {
	const { partyId, preferencesTypeCd, acceptedTermsFileNm, acceptedTermsTs } =
		queryParams

	try {
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const response = await httpPut(
			HTTPDestinationType.EXTERNAL,
			`${apiBaseUrl}/api/users/userid/preferences`,
			{
				token,
				query: { partyId, preferencesTypeCd },
				body:
					preferencesTypeCd === 'CORE'
						? {
								companyId: partyId,
								preferences: {
									preferencesTypeCd,
									termsConditions: {
										acceptedTermsFileNm,
										acceptedTermsTs,
									},
								},
							}
						: queryParams,
			}
		)
		return response
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error updating core user preferences')
	}
}

/**
 * @async
 * @function putUserBusIntelPrefs
 * @description Updates user business intelligence preferences by sending a PUT request to the specified API endpoint.
 *
 * @param {UserBusIntelPrefsQuery} queryParams - Query parameters including partyId and preferencesTypeCd.
 * @param {UserBusIntelPreferences | null} busIntelPreferences - Existing business intelligence preferences data.  If null, default values will be used.
 *
 * @throws {Error} If an error occurs during the API request, an error is thrown with the corresponding message.
 */
export async function putUserBusIntelPrefs(
	queryParams: UserBusIntelPrefsQuery,
	busIntelPreferences?: UserBusIntelPreferences,
	token?: string
) {
	const { partyId, preferencesTypeCd } = queryParams

	let metricTypeCd =
		busIntelPreferences?.preferences?.performance?.header?.metricTypeCd

	// ! clean up salesvolume,
	// change throughout to match TRXAM
	// this is a temporary fix to handle the metricTypeCd
	// possible values from UI for metricTypeCd: ["SALESVOLUME", "SIDES", "GCI",]
	if (metricTypeCd) {
		if (metricTypeCd === 'SALESVOLUME') {
			metricTypeCd = 'TRXAM'
		}
	}

	try {
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const response = await httpPut(
			HTTPDestinationType.EXTERNAL,
			`${apiBaseUrl}/api/users/userid/preferences`,
			{
				token,
				query: { partyId, preferencesTypeCd },
				body: {
					...busIntelPreferences!,
					preferences: {
						preferencesTypeCd: 'BUSINTEL',
						...busIntelPreferences?.preferences,
						performance: {
							...busIntelPreferences?.preferences?.performance,
							header: {
								...busIntelPreferences?.preferences?.performance?.header,
								metricTypeCd,
							},
						},
					},
				},
			}
		)
		return { ...DEFAULTBUSINTELPREFS, ...(response as UserBusIntelPreferences) }
	} catch (error: unknown) {
		if (error instanceof Error) {
			console.error(
				'Failed to update business intelligence preferences:',
				error.message
			)
		}

		return busIntelPreferences
	}
}

export async function putUserAgentProdPrefs(
	queryParams: UserBusIntelPrefsQuery,
	agentProdPreferences?: UserAgentProdPreferences,
	token?: string
) {
	const { partyId, preferencesTypeCd } = queryParams

	let metricTypeCd =
		agentProdPreferences?.preferences?.agentProductivityPrefs?.metricTypeCd

	// ! clean up salesvolume,
	// change throughout to match TRXAM
	// this is a temporary fix to handle the metricTypeCd
	// possible values from UI for metricTypeCd: ["SALESVOLUME", "SIDES", "GCI",]
	if (metricTypeCd) {
		if (metricTypeCd === 'SALESVOLUME') {
			metricTypeCd = 'TRXAM'
		}
	}

	try {
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const response = await httpPut(
			HTTPDestinationType.EXTERNAL,
			`${apiBaseUrl}/api/users/userid/preferences`,
			{
				token,
				query: { partyId, preferencesTypeCd },
				body: {
					...agentProdPreferences!,
					preferences: {
						preferencesTypeCd: 'AGENTPROD',
						...agentProdPreferences?.preferences,
						agentProductivityPrefs: {
							...agentProdPreferences?.preferences?.agentProductivityPrefs,
							metricTypeCd,
						},
					},
				},
			}
		)
		return {
			...DEFAULTAGENTPRODPREFS,
			...(response as UserAgentProdPreferences),
		}
	} catch (error: unknown) {
		if (error instanceof Error) {
			console.error(
				'Failed to update business intelligence preferences:',
				error.message
			)
		}

		return agentProdPreferences
	}
}

/**
 * @async
 * @function putUserRecruitPrefs
 * @description Updates user recruit preferences by sending a PUT request to the specified API endpoint.
 *
 * @param {UserRecruitPrefsQuery} queryParams - Query parameters including partyId and preferencesTypeCd.
 *
 * @throws {Error} If an error occurs during the API request, an error is thrown with the corresponding message.
 */
export async function putUserRecruitPrefs(
	queryParams: UserRecruitPrefsQuery,
	recruitPreferences?: UserRecruitPreferences,
	token?: string
) {
	try {
		const { partyId, preferencesTypeCd } = queryParams
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const response = await httpPut(
			HTTPDestinationType.EXTERNAL,
			`${apiBaseUrl}/api/users/userid/preferences`,
			{
				token,
				query: { partyId, preferencesTypeCd },
				body: {
					...recruitPreferences,
				},
			}
		)
		return { ...DEFAULTRECRUITPREFS, ...(response as UserRecruitPreferences) }
	} catch (error: unknown) {
		if (error instanceof Error) {
			console.error('Failed to update recruitment preferences:', error.message)
		}

		return recruitPreferences
	}
}

/**
 * Fetches the core user preferences for a given party ID and preferences type.
 *
 * @param {object} params - The parameters required to fetch user preferences.
 * @param {string} params.partyId - The unique identifier for the party (e.g., user or organization).
 * @param {string} params.preferencesTypeCd - The type of preferences to fetch (e.g., 'CORE').
 * @param {boolean} [staticContentFromS3=false] - Indicates whether the preferences should be fetched from static content stored in S3.
 * @returns {Promise<object>} A promise that resolves to the user's core preferences object.
 *
 * @throws {Error} Throws an error if the API call fails or the response is invalid.
 *
 */
export async function getCoreUserPreferences(
	queryParams: UserCorePrefsQuery,
	token?: string
): Promise<UserCorePreferences> {
	try {
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const response = await httpGet(
			HTTPDestinationType.EXTERNAL,
			`${apiBaseUrl}/api/users/userid/preferences`,
			{ query: queryParams, token }
		)
		return response as UserCorePreferences
	} catch (error: unknown) {
		if (error instanceof Error) {
			console.error('[UserPreferences] Failed to get core user preferences:', error.message)
		} else {
			console.error('[UserPreferences] An unknown error occurred while fetching core user preferences.')
		}
		return null as unknown as UserCorePreferences
	}
}

/**
 * Retrieves business intelligence preferences for a user from the API.
 * If the API request fails, returns default values based on the provided query parameters.
 *
 * @param queryParams - The query parameters for fetching user preferences
 * @param queryParams.partyId - The ID of the party (company) to retrieve preferences for
 * @param queryParams.preferencesTypeCd - The type code for the preferences
 * @param queryParams.metricTypeCd - Optional. The metric type code (defaults to 'GCI')
 * @param queryParams.metricPeriodTypeCd - Optional. The metric period type code (defaults to 'YTD')
 * @param queryParams.gciCategoryCd - Optional. The GCI category code (defaults to 'UNK')
 *
 * @returns A promise that resolves to user business intelligence preferences.
 * If the API call fails, returns a default response constructed from the provided query parameters.
 *
 * @throws Logs error but doesn't throw - instead returns default values when API calls fail
 */
export async function getBusIntelUserPreferences(
	queryParams: UserBusIntelPrefsQuery,
	token?: string
): Promise<UserBusIntelPreferences | UserAgentProdPreferences | null> {
	try {
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const response = await httpGet(
			HTTPDestinationType.EXTERNAL,
			`${apiBaseUrl}/api/users/userid/preferences`,
			{ query: queryParams, token }
		)
		if (queryParams.preferencesTypeCd === 'BUSINTEL') {
			// Ensure the preferencesTypeCd is set to 'BUSINTEL' for default values
			return {
				...DEFAULTBUSINTELPREFS,
				...(response as UserBusIntelPreferences),
			}
		} else {
			return {
				...DEFAULTAGENTPRODPREFS,
				...(response as UserAgentProdPreferences),
			}
		}
	} catch (error: unknown) {
		if (error instanceof Error) {
			console.error(
				'Error GET business intelligence user preferences:',
				error.message
			)
		}

		if (queryParams.preferencesTypeCd === 'BUSINTEL') {
			// Ensure the preferencesTypeCd is set to 'BUSINTEL' for default values
			return DEFAULTBUSINTELPREFS
		} else {
			return DEFAULTAGENTPRODPREFS
		}
	}
}

/**
 * Retrieves recruit preferences for a user from the API.
 * If the API request fails, returns default values based on the provided query parameters.
 *
 * @param queryParams - The query parameters for fetching user preferences
 * @param queryParams.partyId - The ID of the party (company) to retrieve preferences for
 * @param queryParams.preferencesTypeCd - The type code for the preferences
 *
 * @returns A promise that resolves to user recruit preferences.
 * If the API call fails, returns a default response constructed from the provided query parameters.
 *
 * @throws Logs error but doesn't throw - instead returns default values when API calls fail
 */
export async function getRecruitUserPreferences(
	queryParams: UserRecruitPrefsQuery,
	token?: string
): Promise<UserRecruitPreferences | null> {
	try {
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const response = await httpGet(
			HTTPDestinationType.EXTERNAL,
			`${apiBaseUrl}/api/users/userid/preferences`,
			{ query: queryParams, token }
		)
		return { ...DEFAULTRECRUITPREFS, ...(response as UserRecruitPreferences) }
	} catch (error: unknown) {
		if (error instanceof Error) {
			console.warn('Failed to get recruit preferences:', error.message)
		}

		return DEFAULTRECRUITPREFS
	}
}

/**
 * Utility function to check if the user has accepted the "terms and conditions".
 *
 * @param partyId - The ID of the party (user or company).
 * @returns A promise that resolves to `true` if the terms are accepted, otherwise `false`.
 * @throws An error if the API call fails or if the response is null 400 indicating user
 * exist but never accepted terms.
 */
export async function getLastAcceptedTermsVersion(
	partyId: string,
	token?: string
): Promise<string> {
	try {
		const userPrefs = await getCoreUserPreferences(
			{
				partyId,
				preferencesTypeCd: 'CORE',
			},
			token
		)
		return userPrefs?.preferences?.termsConditions?.acceptedTermsFileNm ?? '-1'
	} catch (error) {
		console.error('Error fetching last accepted terms version:', error)
		return '-1'
	}
}

/**
 * Utility function to check if the user has accepted the "terms and conditions".
 *
 * @param partyId - The ID of the party (user or company).
 * @returns A promise that resolves to `true` if the terms are accepted, otherwise `false`.
 * @throws An error if the API call fails or if the response is null 400 indicating user
 * exist but never accepted terms.
 */
export async function hasAcceptedTerms(
	partyId: string,
	token?: string
): Promise<boolean> {
	try {
		const latestVersion: string = await getTncVersion()
		const latestAcceptedVersion: string = await getLastAcceptedTermsVersion(
			partyId,
			token
		)
		return latestVersion === latestAcceptedVersion
	} catch (error) {
		console.error('Error checking accepted terms:', error)
		return false
	}
}
